import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, Send, Filter, CheckCircle2, XCircle, EyeOff, Copy, Loader2, Activity, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { talentPosts, talentTemplates, libraries, statuses, talents } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { Table, Pagination, Button, Autocomplete } from '@anyemp/ui-kit';
import { PAGINATION_OPTIONS } from '../utils/paginationConstants';
import { loadColumnsStateFromLS, useTableColumns } from '../utils/tableColumnsPersistence';
import SearchInput from '../components/UI/SearchInput';
import ConfirmDialog from '../components/UI/ConfirmDialog';
import StatusSelect from '../components/UI/StatusSelect';
import TalentPostModal from '../components/TalentPost/TalentPostModal';
import PublishModal from '../components/TalentPost/PublishModal';
import Avatar from '../components/UI/Avatar';
import ActivityFitPanel from '../components/UI/ActivityFitPanel';
import SavedFiltersDropdown from '../components/SavedFilters/SavedFiltersDropdown';
import { PAGE_KEYS } from '../constants/savedFilterPageKeys';
import useResponsiveViewPreference from '../hooks/useResponsiveViewPreference';
import useLatestRequest from '../hooks/useLatestRequest';
import DataViewToggle from '../components/UI/DataViewToggle';
import { CardSortControls, RecordCard, RecordCardField, RecordCardFields, RecordCardHeader, ResponsiveCardGrid } from '../components/UI/ResponsiveRecordCards';
import { TALENT_POST_CARD_SORT_OPTIONS } from '../utils/cardSortOptions';
import CardActionsMenu from '../components/UI/CardActionsMenu';
import ModalPortal from '../components/UI/ModalPortal';
import ModalFrame from '../components/UI/ModalFrame';

const SAVED_FILTER_KEYS = [
  'search', 'talent_id', 'template_id', 'inner_client_id',
  'status_id', 'publish_status', 'translation_id',
  'profession_filter', 'responsibility_filter',
];

const PUBLISH_STATUS_OPTIONS = [
  { value: 'published', label: 'Published' },
  { value: 'unpublished', label: 'Unpublished' },
  { value: 'draft', label: 'Draft' }
];

const UserCell = ({ user, fallback }) => {
  if (!user) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary">{fallback || '—'}</span>;
  const displayName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || '—';
  return (
    <div className="flex items-center gap-1.5">
      <Avatar avatar={user.avatar} name={displayName} size="xs" />
      <span>{displayName}</span>
    </div>
  );
};

const GenerationBadge = ({ status }) => {
  if (!status || status === 'idle') return null;
  const map = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    done: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  };
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${map[status] || ''}`}>
      {status}
    </span>
  );
};

const BASE_COLUMNS = [
  { key: 'name', label: 'Name', sortable: true, alwaysPinned: true, alwaysVisible: true },
  { key: 'talent_id', label: 'Talent', sortable: false },
  { key: 'template_id', label: 'Template', sortable: false },
  { key: 'tool', label: 'Tool', sortable: false },
  { key: 'blocks_config', label: 'Blocks', sortable: false },
  { key: 'recipient', label: 'Recipient', sortable: false },
  { key: 'inner_client', label: 'Inner Client', sortable: false },
  { key: 'professions', label: 'Professions', sortable: false },
  { key: 'responsibilities', label: 'Responsibilities', sortable: false },
  { key: 'status_id', label: 'Status', sortable: false },
  { key: 'published', label: 'Published', sortable: false },
  { key: 'translation_id', label: 'Translation', sortable: false },
  { key: 'full_post', label: 'Content', sortable: false },
  { key: 'planned_date', label: 'Planned', sortable: true },
  { key: 'end_date', label: 'End Date', sortable: false },
  { key: 'created_by', label: 'Created by', sortable: false },
  { key: 'actions', label: 'Actions', sortable: false },
];

const TalentPosts = () => {
  const location = useLocation()
  const { hasPermission } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });
  const [filters, setFilters] = useState({
    search: '',
    talent_id: '',
    template_id: '',
    inner_client_id: '',
    status_id: '',
    publish_status: '',
    translation_id: '',
    profession_filter: '',
    responsibility_filter: '',
    page: 1,
    limit: 20,
    sort_by: 'created_at',
    sort_order: 'DESC'
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [duplicatingPost, setDuplicatingPost] = useState(null);
  const [loadingActionId, setLoadingActionId] = useState(null);
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishingPost, setPublishingPost] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isActivityFitOpen, setIsActivityFitOpen] = useState(false);

  // Inline status editing
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(null);
  const [statusRaw, setStatusRaw] = useState([]);

  // Content preview modal
  const [previewPost, setPreviewPost] = useState(null);
  const { mode: viewMode, selectMode: setViewMode } = useResponsiveViewPreference('talent-posts');

  // Expanded responsibilities rows
  const [expandedRespIds, setExpandedRespIds] = useState({});

  // Lookup options
  const [talentOptions, setTalentOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [langOptions, setLangOptions] = useState([]);
  const [professionOptions, setProfessionOptions] = useState([]);
  const [responsibilityOptions, setResponsibilityOptions] = useState([]);
  const [innerClientOptions, setInnerClientOptions] = useState([]);
  const lookupsLoaded = useRef(false);

  const canCreate = hasPermission('talent-service', 'talent_post', 'create');
  const canUpdate = hasPermission('talent-service', 'talent_post', 'update');
  const canDelete = hasPermission('talent-service', 'talent_post', 'delete');

  useEffect(() => {
    if (!lookupsLoaded.current) loadLookups();
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [filters]);

  const loadLookups = async () => {
    try {
      const [talentsRes, templatesRes, langsRes, statusesRes, professionsRes, similarTermsRes, responsibilitiesRes, similarRespTermsRes, innerClientsRes] = await Promise.all([
        talents.getTalents({ all: true, isCommon: 1 }).catch(() => ({ data: [] })),
        talentTemplates.getAll({ limit: 100, is_draft: false }).catch(() => ({ data: [] })),
        libraries.getLanguages().catch(() => ({ data: [] })),
        statuses.getStatusesByType('talent_post', { isCommon: 1 }).catch(() => ({ data: [] })),
        libraries.getProfessions().catch(() => ({ data: [] })),
        libraries.getAllSimilarTerms().catch(() => ({ data: [] })),
        libraries.getResponsibilities().catch(() => ({ data: [] })),
        libraries.getAllSimilarResponsibilityTerms().catch(() => ({ data: [] })),
        libraries.getInnerClients().catch(() => ({ data: [] }))
      ]);

      const talentList = Array.isArray(talentsRes?.data) ? talentsRes.data : [];
      setTalentOptions(talentList.map(t => ({
        value: String(t.id),
        label: [t.first_name, t.last_name].filter(Boolean).join(' ') || `Talent #${t.id}`
      })));

      const tmplList = Array.isArray(templatesRes?.data) ? templatesRes.data : [];
      setTemplateOptions(tmplList.map(t => ({ value: String(t.id), label: t.name })));

      const langList = Array.isArray(langsRes?.data) ? langsRes.data : (Array.isArray(langsRes) ? langsRes : []);
      setLangOptions(langList.map(l => ({ value: String(l.id), label: l.name || l.title })));

      const statusList = Array.isArray(statusesRes?.data) ? statusesRes.data : (Array.isArray(statusesRes) ? statusesRes : []);
      setStatusRaw(statusList);

      const profList = Array.isArray(professionsRes?.data) ? professionsRes.data : [];
      const simList = Array.isArray(similarTermsRes?.data) ? similarTermsRes.data : [];
      setProfessionOptions([
        { label: 'Professions', options: profList.map(p => ({ value: `prof_${p.id}`, label: p.name || p.title || p.value || `#${p.id}` })) },
        { label: 'Similar Terms', options: simList.map(s => ({ value: `sim_${s.id}`, label: s.value || s.name || s.title || `#${s.id}` })) }
      ]);

      const respList = Array.isArray(responsibilitiesRes?.data) ? responsibilitiesRes.data : [];
      const simRespList = Array.isArray(similarRespTermsRes?.data) ? similarRespTermsRes.data : [];
      setResponsibilityOptions([
        { label: 'Responsibilities', options: respList.map(r => ({ value: `resp_${r.id}`, label: r.name || r.value || r.title || `#${r.id}` })) },
        { label: 'Similar Terms', options: simRespList.map(s => ({ value: `sim_resp_${s.id}`, label: s.value || s.name || s.title || `#${s.id}` })) }
      ]);

      const innerClients = Array.isArray(innerClientsRes?.data) ? innerClientsRes.data : [];
      setInnerClientOptions(innerClients.map(c => ({ value: String(c.id), label: c.name || `#${c.id}` })));

      lookupsLoaded.current = true;
    } catch (err) {
      console.error('Failed to load lookups:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      talent_id: '',
      template_id: '',
      inner_client_id: '',
      status_id: '',
      publish_status: '',
      translation_id: '',
      profession_filter: '',
      responsibility_filter: '',
      page: 1,
      limit: 20,
      sort_by: 'created_at',
      sort_order: 'DESC'
    });
  };

  // Only the latest request may write the table (filters can change faster
  // than the server answers)
  const startListRequest = useLatestRequest();

  const fetchPosts = async () => {
    const isCurrent = startListRequest();
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([k, v]) => k !== 'profession_filter' && k !== 'responsibility_filter' && v !== '' && v !== null && v !== undefined)
      );
      if (filters.profession_filter) {
        if (filters.profession_filter.startsWith('prof_')) {
          params.profession_id = filters.profession_filter.replace('prof_', '');
        } else if (filters.profession_filter.startsWith('sim_')) {
          params.similar_term_id = filters.profession_filter.replace('sim_', '');
        }
      }
      if (filters.responsibility_filter) {
        if (filters.responsibility_filter.startsWith('resp_')) {
          params.responsibility_id = filters.responsibility_filter.replace('resp_', '');
        } else if (filters.responsibility_filter.startsWith('sim_resp_')) {
          params.similar_responsibility_term_id = filters.responsibility_filter.replace('sim_resp_', '');
        }
      }
      const res = await talentPosts.getAll(params);
      if (!isCurrent()) return;
      const data = res?.data || [];
      const meta = res?.meta || {};
      setPosts(Array.isArray(data) ? data : []);
      setPagination({
        current_page: meta.page || 1,
        total_pages: meta.totalPages || 1,
        total_items: meta.count || 0,
        items_per_page: filters.limit
      });
    } catch (err) {
      if (!isCurrent()) return;
      toast.error('Failed to load posts');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await talentPosts.remove(id);
      toast.success('Post deleted');
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (postId, statusId) => {
    setStatusChangeLoading(postId);
    try {
      await talentPosts.update(postId, { status_id: statusId || null });
      const newStatus = statusRaw.find(s => String(s.id) === String(statusId)) || null;
      setPosts(prev => prev.map(p =>
        p.id === postId ? { ...p, status_id: statusId || null, status: newStatus } : p
      ));
      setEditingStatusId(null);
      toast.success('Status updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusChangeLoading(null);
    }
  };

  const handleSort = useCallback((key) => {
    setFilters(prev => ({
      ...prev,
      sort_by: key,
      sort_order: prev.sort_by === key && prev.sort_order === 'ASC' ? 'DESC' : 'ASC',
      page: 1
    }));
  }, []);

  const rowKey = useCallback((post) => post.id, []);

  const renderCell = (column, row) => {
    switch (column.key) {
      case 'name':
        return (
          <Link
            to={`/talent-posts/${row.id}`}
            className="font-medium text-light-text-primary dark:text-dark-text-primary hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            {row.name}
          </Link>
        );
      case 'talent_id':
        if (!row.talent) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        return (
          <Link
            to={`/talents/${row.talent.id}`}
            state={{ from: location.pathname + location.search }}
            className="text-sm text-primary-600 dark:text-primary-400 visited:text-purple-700 dark:visited:text-purple-400 hover:underline"
          >
            {[row.talent.first_name, row.talent.last_name].filter(Boolean).join(' ') || `#${row.talent.id}`}
          </Link>
        );
      case 'template_id':
        if (!row.template) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        return (
          <Link
            to={`/talent-templates/${row.template.id}`}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate max-w-[120px] block"
            title={row.template.name}
          >
            {row.template.name}
          </Link>
        );
      case 'tool': {
        const tool = row.tool;
        if (!tool) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        return (
          <div className="flex items-center gap-1.5">
            {tool.icon && <img src={tool.icon} alt="" className="w-4 h-4 object-contain flex-shrink-0" />}
            <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{tool.name || `#${tool.id}`}</span>
          </div>
        );
      }
      case 'blocks_config': {
        const bc = row.blocks_config || {};
        const activeBlocks = Object.entries(bc).filter(([, v]) => v === true).map(([k]) => k);
        if (activeBlocks.length === 0) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        return (
          <div className="flex flex-wrap gap-1 max-w-[140px]">
            {activeBlocks.map(b => (
              <span key={b} className="px-1 py-0.5 text-xs rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {b}
              </span>
            ))}
          </div>
        );
      }
      case 'recipient': {
        const acc = row.accountData;
        const social = row.socialAccountData;
        if (!acc && !social) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        return (
          <div className="flex flex-col gap-0.5">
            {acc && <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary" title="Account">{acc.name || acc.login || `#${acc.id}`}</span>}
            {social && <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary" title="Social Account">{social.name || social.login || `#${social.id}`}</span>}
          </div>
        );
      }
      case 'inner_client': {
        const ic = row.inner_client;
        if (!ic) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{ic.name || `#${ic.id}`}</span>;
      }
      case 'professions': {
        const profs = row.postProfessions || [];
        if (profs.length === 0) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        return (
          <div className="flex flex-col gap-0.5 max-w-[140px]">
            {profs.map((p, i) => (
              <span key={i} className="text-xs text-light-text-secondary dark:text-dark-text-secondary truncate" title={p.profession_name}>
                {p.is_similar && <span className="text-purple-500 mr-0.5">~</span>}{p.profession_name || `#${p.talent_profession_id}`}
              </span>
            ))}
          </div>
        );
      }
      case 'responsibilities': {
        const resps = row.postResponsibilities || [];
        if (resps.length === 0) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        const isExpanded = !!expandedRespIds[row.id];
        const visible = isExpanded ? resps : resps.slice(0, 3);
        return (
          <div className="flex flex-col gap-0.5 max-w-[140px]">
            {visible.map((r, i) => {
              const displayName = r.is_similar && r.term_name ? r.term_name : r.responsibility_name;
              return (
                <span key={i} className="text-xs text-light-text-secondary dark:text-dark-text-secondary truncate" title={displayName}>
                  {r.is_similar && <span className="text-purple-500 mr-0.5">~</span>}{displayName || `#${r.responsibility_id}`}
                </span>
              );
            })}
            {resps.length > 3 && (
              <button
                onClick={() => setExpandedRespIds(prev => ({ ...prev, [row.id]: !prev[row.id] }))}
                className="text-xs text-primary-600 dark:text-primary-400 hover:underline text-left mt-0.5"
              >
                {isExpanded ? 'Show less' : `+${resps.length - 3} more`}
              </button>
            )}
          </div>
        );
      }
      case 'status_id':
        return (
          <StatusSelect
            currentStatus={row.status}
            statusOptions={statusRaw}
            isEditing={editingStatusId === row.id}
            isLoading={statusChangeLoading === row.id}
            onEdit={canUpdate ? () => setEditingStatusId(row.id) : undefined}
            onCancel={() => setEditingStatusId(null)}
            onChange={(statusId) => handleStatusChange(row.id, statusId)}
          />
        );
      case 'published': {
        const statusMap = {
          published: { label: 'Published', cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: <CheckCircle2 size={11} /> },
          unpublished: { label: 'Unpublished', cls: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', icon: <EyeOff size={11} /> },
          draft: { label: 'Draft', cls: 'bg-light-bg-tertiary text-light-text-tertiary dark:bg-dark-bg-tertiary dark:text-dark-text-tertiary', icon: <XCircle size={11} /> }
        };
        const s = statusMap[row.publish_status] || statusMap.draft;
        return (
          <div className="flex flex-col gap-0.5">
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${s.cls}`}>
              {s.icon} {s.label}
            </span>
            {row.publish_date && (row.publish_status === 'published' || row.publish_status === 'unpublished') && (
              <span className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary">
                {new Date(row.publish_date).toLocaleDateString()}
              </span>
            )}
          </div>
        );
      }
      case 'full_post':
        return (
          <div className="flex items-center gap-1.5">
            {row.full_post ? (
              <button
                onClick={() => setPreviewPost(row)}
                className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"
                title="Has content — click to view"
              />
            ) : (
              <span className="w-3 h-3 rounded-full bg-light-border-primary dark:bg-dark-border-primary flex-shrink-0" />
            )}
            <GenerationBadge status={row.generation_status} />
          </div>
        );
      case 'translation_id': {
        const tr = row.translation;
        return (
          <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            {tr ? (tr.name || tr.title || `#${tr.id}`) : (row.translation_id ? `#${row.translation_id}` : '—')}
          </span>
        );
      }
      case 'planned_date':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary">{row.planned_date ? new Date(row.planned_date).toLocaleDateString() : '—'}</span>;
      case 'end_date': {
        if (!row.end_date) return <span className="text-light-text-tertiary dark:text-dark-text-tertiary text-sm">—</span>;
        const isPast = new Date(row.end_date) < new Date();
        return (
          <span className={`text-sm font-medium ${isPast ? 'text-red-500 dark:text-red-400' : 'text-light-text-secondary dark:text-dark-text-secondary'}`}>
            {new Date(row.end_date).toLocaleDateString()}
          </span>
        );
      }
      case 'created_by':
        return <span className="text-sm text-light-text-secondary dark:text-dark-text-secondary"><UserCell user={row.createdByUser} fallback={row.created_by} /></span>;
      default:
        return null;
    }
  };

  const renderActions = (row) => (
    <div className="flex items-center justify-end gap-1">
      <button
        onClick={() => { setPublishingPost(row); setPublishModalOpen(true); }}
        disabled={!row.full_post}
        className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-light-text-tertiary transition-colors hover:bg-light-bg-tertiary hover:text-green-600 disabled:cursor-not-allowed disabled:opacity-30 dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary dark:hover:text-green-400"
        title={row.full_post ? 'Publish' : 'No content yet'}
      >
        <Send size={15} />
      </button>
      {canCreate && (
        <button
          onClick={async () => {
            setLoadingActionId(`dup-${row.id}`);
            try {
              const res = await talentPosts.getById(row.id);
              setDuplicatingPost(res?.data || res);
            } catch { setDuplicatingPost(row); }
            finally { setLoadingActionId(null); }
            setEditingPost(null); setModalOpen(true);
          }}
          disabled={!!loadingActionId}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-light-text-tertiary transition-colors hover:bg-light-bg-tertiary hover:text-purple-600 disabled:opacity-40 dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary dark:hover:text-purple-400"
          title="Duplicate"
        >
          {loadingActionId === `dup-${row.id}`
            ? <Loader2 size={15} className="animate-spin" />
            : <Copy size={15} />}
        </button>
      )}
      {canUpdate && (
        <button
          onClick={async () => {
            setLoadingActionId(`edit-${row.id}`);
            try {
              const res = await talentPosts.getById(row.id);
              setEditingPost(res?.data || res);
            } catch { setEditingPost(row); }
            finally { setLoadingActionId(null); }
            setDuplicatingPost(null); setModalOpen(true);
          }}
          disabled={!!loadingActionId}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-light-text-tertiary transition-colors hover:bg-light-bg-tertiary hover:text-blue-600 disabled:opacity-40 dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary dark:hover:text-blue-400"
          title="Edit"
        >
          {loadingActionId === `edit-${row.id}`
            ? <Loader2 size={15} className="animate-spin" />
            : <Edit size={15} />}
        </button>
      )}
      {canDelete && (
        <button
          onClick={() => setDeleteTarget(row)}
          disabled={deletingId === row.id}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-light-text-tertiary transition-colors hover:bg-light-bg-tertiary hover:text-red-600 disabled:opacity-50 dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary dark:hover:text-red-400"
          title="Delete"
        >
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );

  const columns = useMemo(
    () => BASE_COLUMNS.map((col) => ({
      ...col,
      render: col.key === 'actions'
        ? (row) => renderActions(row)
        : (row) => renderCell(col, row),
    })),
    [canUpdate, canDelete, deletingId, loadingActionId, editingStatusId, statusChangeLoading, expandedRespIds, statusRaw, location.pathname, location.search]
  );

  const sorting = useMemo(
    () => ({ sort_by: filters.sort_by, sort_order: filters.sort_order }),
    [filters.sort_by, filters.sort_order]
  );

  const tableColumnsState = useTableColumns(columns, {
    ...loadColumnsStateFromLS('talentPosts_columns', { defaultPinned: ['name'] }),
    onChange: (state) => {
      try { localStorage.setItem('talentPosts_columns', JSON.stringify(state)); } catch {}
    },
  });

  const hasActiveFilters = filters.search || filters.talent_id || filters.template_id || filters.inner_client_id || filters.status_id || filters.publish_status || filters.translation_id || filters.profession_filter || filters.responsibility_filter;

  return (
    <div className="min-h-full space-y-6 bg-light-bg-primary dark:bg-dark-bg-primary">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Send className="text-blue-600" size={24} />
          <h1 className="text-2xl font-bold text-light-text-primary dark:text-dark-text-primary">Talent Posts</h1>
          <span className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">({pagination.total_items})</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setIsActivityFitOpen(true)}
            className="flex min-h-10 items-center gap-1.5 rounded-md border border-light-border-primary px-3 py-1.5 text-sm text-light-text-secondary transition-colors hover:bg-light-bg-secondary hover:text-light-text-primary dark:border-dark-border-primary dark:text-dark-text-secondary dark:hover:bg-dark-bg-secondary dark:hover:text-dark-text-primary"
          >
            <Activity size={16} />
            Activity Feed
          </button>
          {canCreate && (
            <button
              onClick={() => { setEditingPost(null); setModalOpen(true); }}
              className="flex min-h-10 items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm text-white transition-colors hover:bg-primary-700"
            >
              <Plus size={16} />
              Add Post
            </button>
          )}
          <DataViewToggle value={viewMode} onChange={setViewMode} />
        </div>
      </div>

      {/* Filters panel */}
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-sm border border-light-border-primary dark:border-dark-border-primary p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">Filters</h3>
          <div className="flex flex-wrap items-center gap-3">
            <SavedFiltersDropdown
              pageKey={PAGE_KEYS.TALENT_POSTS}
              currentFilters={filters}
              applyKeys={SAVED_FILTER_KEYS}
              onApply={(saved) => setFilters(prev => ({ ...prev, ...saved, page: 1 }))}
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
            >
              <Filter size={14} />
              {showFilters ? 'Hide' : 'Show'} Filters
              {hasActiveFilters && !showFilters && (
                <span className="ml-1 w-2 h-2 rounded-full bg-primary-500 inline-block" />
              )}
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Search</label>
                <SearchInput
                  value={filters.search}
                  onChange={val => handleFilterChange('search', val)}
                  placeholder="Search posts..."
                  debounceTime={300}
                  showIcon
                />
              </div>

              <Autocomplete
                clearable
                fullWidth
                label="Talent"
                options={talentOptions}
                value={filters.talent_id}
                onChange={val => handleFilterChange('talent_id', val || '')}
                placeholder="Any talent..."
              />

              <Autocomplete
                clearable
                fullWidth
                label="Template"
                options={templateOptions}
                value={filters.template_id}
                onChange={val => handleFilterChange('template_id', val || '')}
                placeholder="Any template..."
              />

              <Autocomplete
                clearable
                fullWidth
                label="Inner Client"
                options={innerClientOptions}
                value={filters.inner_client_id}
                onChange={val => handleFilterChange('inner_client_id', val || '')}
                placeholder="Any inner client..."
              />

              <Autocomplete
                clearable
                fullWidth
                label="Status"
                options={statusRaw.map(s => ({ value: String(s.id), label: s.name }))}
                value={filters.status_id}
                onChange={val => handleFilterChange('status_id', val || '')}
                placeholder="Any status..."
              />

              <Autocomplete
                clearable
                fullWidth
                label="Publish Status"
                options={PUBLISH_STATUS_OPTIONS}
                value={filters.publish_status}
                onChange={val => handleFilterChange('publish_status', val || '')}
                placeholder="Any..."
              />

              <Autocomplete
                clearable
                fullWidth
                label="Translation"
                options={langOptions}
                value={filters.translation_id}
                onChange={val => handleFilterChange('translation_id', val || '')}
                placeholder="Any language..."
              />

              <Autocomplete
                clearable
                fullWidth
                label="Profession"
                options={professionOptions}
                value={filters.profession_filter}
                onChange={val => handleFilterChange('profession_filter', val || '')}
                placeholder="Any profession..."
              />

              <Autocomplete
                clearable
                fullWidth
                label="Responsibility"
                options={responsibilityOptions}
                value={filters.responsibility_filter}
                onChange={val => handleFilterChange('responsibility_filter', val || '')}
                placeholder="Any responsibility..."
              />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleResetFilters}
                className="text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
              >
                Reset All Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {viewMode === 'cards' && (
        <div className="space-y-4">
          <CardSortControls
            options={TALENT_POST_CARD_SORT_OPTIONS}
            sortBy={filters.sort_by}
            sortOrder={filters.sort_order}
            onSortByChange={(sort_by) => setFilters(prev => ({ ...prev, sort_by, page: 1 }))}
            onSortOrderChange={(sort_order) => setFilters(prev => ({ ...prev, sort_order, page: 1 }))}
          />
          {loading ? <div className="py-12 text-center text-light-text-tertiary">Loading posts…</div> : posts.length ? (
            <ResponsiveCardGrid>
              {posts.map((row) => (
                <RecordCard key={row.id}>
                  <RecordCardHeader title={<Link to={`/talent-posts/${row.id}`} className="hover:text-primary-600">{row.name}</Link>} badge={renderCell({ key: 'published' }, row)} actions={<CardActionsMenu>{renderActions(row)}</CardActionsMenu>} />
                  <RecordCardFields>
                    <RecordCardField label="Talent">{row.talent ? <Link to={`/talents/${row.talent.id}`} className="text-primary-600 hover:underline">{row.talent.name || `${row.talent.last_name || ''} ${row.talent.first_name || ''}`.trim()}</Link> : '—'}</RecordCardField>
                    <RecordCardField label="Template">{row.template?.name || '—'}</RecordCardField>
                    <RecordCardField label="Status">{renderCell({ key: 'status_id' }, row)}</RecordCardField>
                    <RecordCardField label="Planned">{row.planned_date ? new Date(row.planned_date).toLocaleDateString() : '—'}</RecordCardField>
                    <RecordCardField label="End date">{row.end_date ? new Date(row.end_date).toLocaleDateString() : '—'}</RecordCardField>
                  </RecordCardFields>
                </RecordCard>
              ))}
            </ResponsiveCardGrid>
          ) : <div className="py-12 text-center text-light-text-tertiary">No posts found</div>}
          <div className="overflow-x-auto"><Pagination currentPage={pagination.current_page} totalPages={pagination.total_pages} totalItems={pagination.total_items} itemsPerPage={pagination.items_per_page} onPageChange={page => setFilters(prev => ({ ...prev, page }))} showResults="range" perPageOptions={PAGINATION_OPTIONS} perPage={filters.limit} onPerPageChange={limit => setFilters(prev => ({ ...prev, limit: parseInt(limit), page: 1 }))} showOnSinglePage hasFirstLast hasGoToPage /></div>
        </div>
      )}
      {viewMode === 'table' && <Table
        columns={columns}
        data={posts}
        isLoading={loading}
        rowKey={rowKey}
        sorting={sorting}
        onSort={handleSort}
        columnMenu
        {...tableColumnsState}
        emptyState={
          <div className="py-8 text-center text-light-text-tertiary dark:text-dark-text-tertiary">
            No posts found
          </div>
        }
        footer={
          <Pagination
            currentPage={pagination.current_page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total_items}
            itemsPerPage={pagination.items_per_page}
            onPageChange={page => setFilters(prev => ({ ...prev, page }))}
            showResults="range"
            perPageOptions={PAGINATION_OPTIONS}
            perPage={filters.limit}
            onPerPageChange={limit => setFilters(prev => ({ ...prev, limit: parseInt(limit), page: 1 }))}
            showOnSinglePage
            hasFirstLast
            hasGoToPage
          />
        }
      />}

      {previewPost && (
        <ModalPortal onClose={() => setPreviewPost(null)} ariaLabel="Talent post preview">
          <ModalFrame size="md" className="-m-3 w-[calc(100%+1.5rem)] rounded-none sm:m-0 sm:w-full sm:rounded-lg">
            <ModalFrame.Header className="items-center">
              <span className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary truncate">
                {previewPost.name}
              </span>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="outlined"
                  color="neutral"
                  size="small"
                  onClick={() => { navigator.clipboard.writeText(previewPost.full_post); toast.success('Copied'); }}
                >
                  <Copy size={12} className="mr-1 inline-block align-[-2px]" /> Copy
                </Button>
                <button type="button" onClick={() => setPreviewPost(null)} aria-label="Close" className="inline-flex h-10 w-10 items-center justify-center rounded-md text-light-text-tertiary hover:bg-light-bg-secondary dark:text-dark-text-tertiary dark:hover:bg-dark-bg-tertiary">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </ModalFrame.Header>
            <ModalFrame.Body>
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown>{previewPost.full_post}</ReactMarkdown>
            </div>
            </ModalFrame.Body>
            <ModalFrame.Footer className="flex justify-end">
              <Button type="button" variant="outlined" color="neutral" onClick={() => setPreviewPost(null)}>
                Close
              </Button>
            </ModalFrame.Footer>
          </ModalFrame>
        </ModalPortal>
      )}

      <TalentPostModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingPost(null); setDuplicatingPost(null); }}
        post={editingPost}
        duplicateFrom={duplicatingPost}
        onSaved={fetchPosts}
      />
      <PublishModal
        isOpen={publishModalOpen}
        onClose={() => { setPublishModalOpen(false); setPublishingPost(null); }}
        post={publishingPost}
        onPublished={fetchPosts}
      />
      <ActivityFitPanel
        isOpen={isActivityFitOpen}
        onClose={() => setIsActivityFitOpen(false)}
        entityType="TalentPost"
        title="Activity Feed — Talent Posts"
      />
      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget.id)}
        title="Delete Talent Post"
        message={<>Delete post <strong>{deleteTarget?.name}</strong>?</>}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={deletingId === deleteTarget?.id}
      />
    </div>
  );
};

export default TalentPosts;
